import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Pressable } from 'native-base';
import React, { useEffect } from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { RiskRating } from '../../../../../../../../../components/RiskRating';
import { TableContentItemText } from '../../../../../../../../../components/TableContent';
import { useCoesAssetController } from '../../../../../hooks/products/Coes';
import { useCoesBillingTypeController } from '../../../../../hooks/products/Coes/billing';
import { TextSkeleton } from '../../../../AssetsLoading';
import { AssetTitleWithSubtitle } from '../../../components/AssetTitleWithSubtitle';
import { LateralBar } from '../../../components/LateralBar';

import { CoesItemSelectedTypes } from '.';

export const CoesItemSelectedDesktop: React.FC<CoesItemSelectedTypes.Props> = ({
  offer,
  onUpdate,
}) => {
  const id = offer.productIdNormalized;
  const {
    control,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    isFocused,
    currentValueFormatted,
    currentValue,
  } = useCoesAssetController(offer);

  const {
    refresher,
    billingTypeSelected,
    onChangeBillingTypeSelected,
    billingTypeOptions,
    applicationIn,
    isLoading,
  } = useCoesBillingTypeController({ product: offer });

  useEffect(() => {
    if (offer.orderValue === currentValue || !currentValue) {
      return;
    }
    const updatedOffer = { ...offer, orderValue: currentValue || 0 };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  useEffect(() => {
    if (offer.billingTypeId === billingTypeSelected || !billingTypeSelected) {
      return;
    }
    const updatedOffer = { ...offer, billingTypeId: billingTypeSelected };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingTypeSelected]);

  return (
    <UI.List testId="selected-product" height={120}>
      <LateralBar
        backgroundColor={Color['product.estruturados']}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <HStack flex={1} justifyContent="space-between">
        <UI.ListItem testId="selected-product-name" title="Operação" stackProps={{ maxW: '1/6' }}>
          <AssetTitleWithSubtitle
            title={offer.productNameNormalized}
            subtitle={offer.strategyNormalized}
          />
        </UI.ListItem>
        <UI.ListItem testId="selected-product-maturityDate" title="Vencimento">
          <TableContentItemText>{offer.maturityDateNormalized}</TableContentItemText>
        </UI.ListItem>
        <UI.ListItem testId="selected-product-risk" title="Risco">
          <RiskRating
            currentRiskRating={offer.riskRatingScoreNormalized}
            tooltip={offer.riskRatingNormalized}
          />
        </UI.ListItem>
        <UI.ListItem
          testId="selected-product-application-in"
          title="Aplicação em"
          stackProps={{ minW: '80px' }}>
          {isLoading ? (
            <TextSkeleton w="80%" />
          ) : (
            <TableContentItemText>{applicationIn || offer.applicationIn}</TableContentItemText>
          )}
        </UI.ListItem>
        <UI.ListItem testId="selected-product-application-minimum" title="Aplicação mínima">
          <TableContentItemText>
            {Utils.Mask.Money(offer.minimumApplicationNormalized)}
          </TableContentItemText>
        </UI.ListItem>
        <VStack>
          <Pressable onPress={refresher}>
            <HStack space="inline.nano">
              <UI.Text variant="semibold">Tipo de boletagem</UI.Text>
              <UI.Icon name="Refresh" size="xxxs" color={'brand.tertiary.principal'} />
            </HStack>
          </Pressable>
          <UI.Select
            testID="select-billing-option"
            triggerProps={{
              isDisabled: isLoading || billingTypeOptions.length === 0,
              _disabled: { opacity: 0.2 },
            }}
            options={billingTypeOptions}
            onValueChange={onChangeBillingTypeSelected}
            selectedValue={billingTypeSelected}
            placeholder="Selecione"
          />
        </VStack>
        <UI.ListItem testId="selected-product-name" title="Valor aplicado">
          <Stack pr={Spacing['inline.xxxs']} w="222px">
            <InputMoney
              id={`coes-input${id}`}
              name="createAppliedAmount"
              control={control}
              handleFocus={handleInputFocus}
              handleBlur={handleInputBlur}
              handleSubmit={handleSubmit}
              handleClear={handleInputClear}
              currentValue={currentValueFormatted}
              hasFocus={isFocused}
              minValue={offer.minimumApplication}
              customInputProps={{
                ...{ ...(isFocused && hasError && { error: hasError }) },
                ...{ ...(isFocused && !hasError && { helpText }) },
              }}
            />
          </Stack>
        </UI.ListItem>
      </HStack>
    </UI.List>
  );
};
